import React from 'react';
import { Redirect } from 'umi';
import { notification } from 'antd';
import { stringify } from 'querystring';
import { setUser } from '@/utils/authority';
import { getToken, setToken, clearToken } from '@/utils/token';
import { fetchCurUser, parseToken } from '@/services/api';
import { isRefreshing, shouldRefreshSoon } from '@/utils/request';
import PromiseRender from './PromiseRender';
import { CURRENT } from './renderAuthorize';
/**
 * 通用权限检查方法
 * Common check permissions method
 * @param { 权限判定 | Permission judgment } authority
 * @param { 你的权限 | Your permission description } currentAuthority
 * @param { 通过的组件 | Passing components } target
 * @param { 未通过的组件 | no pass components } Exception
 */
const checkPermissions = (authority, currentAuthority, target, Exception) => {
  const { props = {} } = target || {};
  const { pathname, query } = props.location || {};
  const queryString = stringify({
    redirect: window.location.href,
  });
  if (query && query.access_token) {
    parseToken(query.access_token)
      .then(res => {
        if (res && res.token) {
          const [, temp] = res.token.split('Bearer ');
          const token = {
            access_token: temp,
            token_type: 'Bearer',
            expires_in: 1 * 24 * 3600, // hmac token 默认1天过期
          };
          setToken(token);
          return fetchCurUser();
        }
        return null;
      })
      .then(user => {
        if (user && user.id) {
          setUser(user);
          window.location.href = pathname;
        }
      });
  }

  // 登录页不需要检查 token
  if (pathname === '/user/login') {
    return target;
  }

  // 如果 refresh 正在进行，暂时允许通过，不检查 token，等待 refresh 完成
  // refresh 完成后，如果 token 仍然无效，会在下次路由切换时被检查到
  if (isRefreshing()) {
    return target;
  }

  const token = getToken();

  // 如果 token 不存在，直接跳转登录
  if (!token || !token.access_token) {
    notification.error({
      message: '权限失效，请重新登录',
    });
    setUser({});
    clearToken();
    return <Redirect to={`/user/login?${queryString}`} />;
  }

  // 检查 token 是否过期
  const isExpired = (() => {
    if (!token?.token_created_at || !token?.expires_in) return false;
    const expiresInMs = Number(token.expires_in) * 1000;
    if (!Number.isFinite(expiresInMs) || expiresInMs <= 0) return false;
    const createdAt = Number(token.token_created_at);
    if (!createdAt) return false;
    const expiresAt = createdAt + expiresInMs;
    return expiresAt <= Date.now();
  })();

  // 如果 token 过期，但应该刷新（使用和拦截器相同的逻辑），暂时允许通过
  // 让拦截器在请求时触发 refresh，如果 refresh 失败，下次路由切换时会再次检查到
  if (isExpired) {
    const needRefresh = shouldRefreshSoon(token);
    if (needRefresh) {
      // token 过期但应该刷新，暂时允许通过，让拦截器处理刷新
      return target;
    }
    // token 过期且无法刷新（比如 expires_in 无效），直接跳转登录
    notification.error({
      message: '权限失效，请重新登录',
    });
    setUser({});
    clearToken();
    return <Redirect to={`/user/login?${queryString}`} />;
  }

  // 没有判定权限.默认查看所有
  // Retirement authority, return target;
  if (!authority) {
    return target;
  } // 数组处理
  if (Array.isArray(authority)) {
    if (Array.isArray(currentAuthority)) {
      if (currentAuthority.some(item => authority.includes(item))) {
        return target;
      }
    } else if (authority.includes(currentAuthority)) {
      return target;
    }

    return Exception;
  } // string 处理

  if (typeof authority === 'string') {
    if (Array.isArray(currentAuthority)) {
      if (currentAuthority.some(item => authority === item)) {
        return target;
      }
    } else if (authority === currentAuthority) {
      return target;
    }

    return Exception;
  } // Promise 处理

  if (authority instanceof Promise) {
    return <PromiseRender ok={target} error={Exception} promise={authority} />;
  } // Function 处理

  if (typeof authority === 'function') {
    // eslint-disable-next-line no-useless-catch
    try {
      const bool = authority(currentAuthority); // 函数执行后返回值是 Promise

      if (bool instanceof Promise) {
        return <PromiseRender ok={target} error={Exception} promise={bool} />;
      }

      if (bool) {
        return target;
      }

      return Exception;
    } catch (error) {
      throw error;
    }
  }

  throw new Error('unsupported parameters');
};

export { checkPermissions };

function check(authority, target, Exception) {
  return checkPermissions(authority, CURRENT, target, Exception);
}

export default check;
